import {Card} from "primereact/card";
import QuestionHeader from "../question-header/question-header";
import {RadioButtonGrid, RadioButtonGridProps, RadioButtonGridType} from "../radio-button/radio-button";
import {AnswerInput} from "../answer-input/answer-input";
import {InputText} from 'primereact/inputtext';
import QuestionImageUpload from "../image-upload/question-image-upload/question-image-upload";
import {B2BButton} from "../b2bbutton/b2bbutton";
import {useEffect, useMemo, useState} from "react";
import {
  useCreateAnswerMutation,
  useDeleteImageMutation,
  useDeleteQuestionMutation,
  useGetSurveyQuery,
  useUpdateQuestionMutation,
} from "../../redux/survey/survey.endpoints";
import {InputSwitch, InputSwitchChangeEvent} from "primereact/inputswitch";
import {
  Question,
  QuestionUpdateData,
  SaveStatusEnum,
  ShuffleMode,
  SurveyQuestionType,
  SurveyStatus,
} from "../../redux/survey/survey.types";
import plusIcon from '../../assets/icons/Plus-black.svg';
import {useDispatch, useSelector} from "react-redux";
import {setSaveStateWithDelay} from "../../redux/survey/survey-status.utils";
import {setSaveState} from "../../redux/survey/survey-status.slice";
import '../image-upload/question-image-upload/question-image-upload.css';
import {booleanToShuffleMode, shuffleModeToBoolean} from "../../services/surveys/surveys.helpers";
import {MAX_ANSWER_COUNT} from "../../pages/view-survey/view-survey.constants";
import TooltipIcon from "../tooltip-icon/tooltip-icon.component";
import { RootState } from "../../redux/redux-store";
import FirebaseService from "../../services/firebase/firebase.service";
import {CreateSurveyCopy} from "../../pages/create-survey/create-survey.types";
import { DEFAULT_ANSWER_TITLE } from "../../services/surveys/survey.constants";

interface QuestionCardProps {
  extSurveyId: string;
  id: string;
  elementId?: string;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({ extSurveyId, id, elementId }) => {
  const { rationaleToggle } = FirebaseService.getRemoteConfigJSON<CreateSurveyCopy>('CREATE_SURVEY_COPY');
  const allowFreeform = FirebaseService.getRemoteConfigBool('ALLOW_FREEFORM') ?? false;
  const { data } = useGetSurveyQuery(
    { ext_survey_id: extSurveyId },
    { skip: !extSurveyId }
  );

  const answerTypeButtons = [
    { label: 'Single choice', value: SurveyQuestionType.SingleSelect },
    { label: 'Multiple choice', value: SurveyQuestionType.MultiSelect },
  ];
  if (allowFreeform) {
    answerTypeButtons.push({ label: "Freeform response", value: SurveyQuestionType.Freeform });
  }

  const answerTypeProps: RadioButtonGridProps = {
    extSurveyId: extSurveyId,
    title: 'Types of Answers',
    buttons: answerTypeButtons,
    type: RadioButtonGridType.AnswerType,
  };
  const dispatch = useDispatch();
  const initialIsImageAnswers = useSelector((state: RootState) => Boolean(state.surveyImageTestState?.isImageTest?.[extSurveyId]) ?? false);
  const [question, setQuestion] = useState<Question | undefined>(undefined);
  const [answerType, setAnswerType] = useState<string>('');
  const [updateQuestion, { isSuccess: updateQuestionSuccess }] = useUpdateQuestionMutation();
  const [createAnswer, { isSuccess: createSuccess }] = useCreateAnswerMutation();
  const [deleteQuestion, { isSuccess: deleteQuestionSuccess }] = useDeleteQuestionMutation();
  const [deleteImage] = useDeleteImageMutation();
  const [questionText, setQuestionText] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isImageAnswers, setIsImageAnswers] = useState<boolean>(initialIsImageAnswers);
  const [shuffleMode, setShuffleMode] = useState<ShuffleMode>('random');
  const [allowCustomResponse, setAllowCustomResponse] = useState(false);
  const isFreeform = answerType === SurveyQuestionType.Freeform;

  const { canAddAnswer, addAnswerText } = useMemo(() => {
    if (!data || data.surveyStatus === SurveyStatus.Review) {
      return {canAddAnswer: false, addAnswerText: ''};
    }

    const answerCount = question?.answers?.length || 0;
    const answersRemaining = MAX_ANSWER_COUNT - answerCount;
    if (answersRemaining <= 0) {
      return {canAddAnswer: false, addAnswerText: ''};
    }

    const answerString = answersRemaining === 1 ? 'answer' : 'answers';
    return { canAddAnswer: true, addAnswerText: `Add answer (${MAX_ANSWER_COUNT - answerCount} ${answerString} left)` };
  }, [data, question]);

  useEffect(() => {
    const updatedQuestion = data?.questions.find(q => q.id === id);

    if (!updatedQuestion) {
      return;
    }

    const allImageUrlsNull = updatedQuestion.answers.every(answer => answer.imageUrl === null);
    setIsImageAnswers(isImageAnswers || !allImageUrlsNull);
    setQuestion(updatedQuestion);
    setQuestionText(updatedQuestion.title !== "Untitled_Question" ? updatedQuestion.title || "" : "");
    setAnswerType(updatedQuestion.type);
    setShuffleMode(updatedQuestion.shuffle);
    setAllowCustomResponse(updatedQuestion.hasFeedback);
  }, [data, id]);

  useEffect(() => {
    validateQuestionAndAnswers();
  }, [questionText, question?.answers, data?.brand?.config?.rtr_size, isFreeform, isImageAnswers]);

  useEffect(() => {
    const showSuccess = updateQuestionSuccess || createSuccess || deleteQuestionSuccess;
    showSuccess && setSaveStateWithDelay(dispatch);
  }, [updateQuestionSuccess, createSuccess, deleteQuestionSuccess]);

  const validateQuestionAndAnswers = () => {
    const newWarnings: string[] = [];
    const brandSize = data?.brand?.config?.rtr_size || '300x250';

    // Validate question & answers
    const maxLength = brandSize === '970x250' ? 154 : 85;
    if (questionText.trim().length > maxLength) {
      newWarnings.push(`Question title may be too long for current RTR size`);
    }
    if (brandSize === '300x250') {
      if (isImageAnswers) {
        newWarnings.push('Image answers are not supported for current RTR size');
      }
    }
    if (!isFreeform && question?.answers) {
      const answerCount = question.answers.length;
      question.answers.forEach((answer, index) => {
        const answerText = answer.title || '';
        const answerNumber = index + 1;
        if (brandSize === '300x250') {
          if (answerCount <= 4) {
            if (answerText.trim().length > 40) {
              newWarnings.push(`Answer ${answerNumber} text may be too long`);
            }
          } else if (answerText.trim().length > 17) {
            newWarnings.push(`Answer ${answerNumber}: Text may be too long given 5+ answer choices`);
          }
          if (answerCount > 6) {
            newWarnings.push(`Maximum of 6 answer choices allowed for current RTR size`);
          }
        } else if (brandSize === '970x250') {
          if (isImageAnswers && answerCount > 4) {
            newWarnings.push(`Maximum of 4 image answers allowed for current RTR size`);
          }
          if (answerText !== "Untitled_Answer" && answerText.trim().length > 0) {
            newWarnings.push(`Answer text is not supported for current RTR size, only image answer choices`);
          }
        }
      });
    }
    setWarnings([...new Set(newWarnings)]);
  };

  const internalQuestionUpdate = ({ questionType, shuffleAnswers, hasFeedback }: { questionType?: SurveyQuestionType, shuffleAnswers?: ShuffleMode, hasFeedback?: boolean }) => {
    if (!question) {
      return;
    }

    const finalTitle = questionText.trim().length === 0 ? "Untitled_Question" : questionText;
    const updateData: QuestionUpdateData = {
      ext_survey_id: extSurveyId,
      survey: extSurveyId,
      title: finalTitle,
      question_id: question.id,
      question_order: question.questionOrder,
      shuffle: shuffleAnswers ?? question.shuffle,
      has_feedback: hasFeedback,
    };

    if (questionType) {
      updateData.type = questionType;
    }

    void updateQuestion(updateData);
  };

  const handleAnswerTypeChange = (value: string) => {
    const selectedValue = value as SurveyQuestionType;
    internalQuestionUpdate({ questionType: selectedValue });
  };

  const handleQuestionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSaveState({ status: SaveStatusEnum.Syncing }));
    const value = e.target.value;
    setQuestionText(value);
    if (value.trim().length < 2) {
      setError('Question must be at least 2 characters long');
    } else {
      setError('');
    }
    validateQuestionAndAnswers();
  };

  const handleQuestionTextBlur = () => {
    internalQuestionUpdate({});
  };

  const handleDelete = () => {
    if (!question) return;
    void deleteQuestion({ ext_survey_id: extSurveyId, question_id: question.id || '' });
  };

  const handleAddOption = () => {
    if (question?.answers?.length || 0 < 8) {
      void createAnswerCall();
    }
  };

  const createAnswerCall = async() => {
    if (!question) return;
    const initialAnswerData = {
      title: DEFAULT_ANSWER_TITLE,
      survey: extSurveyId,
      survey_question: question.id || "",
      ext_survey_id: extSurveyId,
    };

    void createAnswer(initialAnswerData);
  };

  const handleIsImageAnswersToggle = async() => {
    if (isImageAnswers) {
      for (const answer of question?.answers || []) {
        if (answer.imageUrl) {
          await handleImageRemove(answer.id);
        }
      }
    }
    setIsImageAnswers(!isImageAnswers);
  };

  const handleImageRemove = async(answerId: string) => {
    try {
      await deleteImage({ image_id: answerId, uuid: extSurveyId, type: "answer" }).unwrap();
    } catch (error) {
      console.error('Error deleting answer image:', error);
    }
  };

  const handleShuffleChange = ({ value }: InputSwitchChangeEvent) => {
    const newShuffleMode = booleanToShuffleMode(value);

    if (newShuffleMode === shuffleMode) {
      return;
    }

    internalQuestionUpdate({ shuffleAnswers: newShuffleMode });
  };

  const handleCustomResponseToggle = ({ value }: InputSwitchChangeEvent) => {
    // connect this to the api later
    if (value === allowCustomResponse) {
      return;
    }

    internalQuestionUpdate({ hasFeedback: value });
  };

  const renderOptionToggles = () => {
    if (data?.surveyStatus === SurveyStatus.Review) {
      return null;
    }

    const isShuffleOn = shuffleModeToBoolean(shuffleMode);

    return (
      <div>
        <div className="flex items-center">
          <div className="upload_question_image flex items-center">
            <InputSwitch
              checked={isShuffleOn}
              onChange={handleShuffleChange}
              className={`custom-switch ${isShuffleOn ? 'p-inputswitch-checked' : ''}`}
            />
          </div>
          <span className="mr-2">
            Randomize answer order
          </span>
          <TooltipIcon
            targetClass="shuffle-toggle"
            message="Shuffle the order of answer choices for each respondent to reduce bias."
          />
        </div>
        <div className="flex items-center">
          <div className="upload_question_image flex items-center">
            <InputSwitch
              checked={isImageAnswers}
              onChange={handleIsImageAnswersToggle}
              className={`custom-switch ${isImageAnswers ? 'p-inputswitch-checked' : ''}`}
            />
          </div>
          <span className="mr-2">
            Answer image(s)
          </span>
          <TooltipIcon
            targetClass="image-toggle"
            message="Attach an image to an individual answer choice."
          />
        </div>
        <div className="flex items-center">
          <div className="upload_question_image flex items-center">
            <InputSwitch
              checked={allowCustomResponse}
              onChange={handleCustomResponseToggle}
              className={`custom-switch ${allowCustomResponse ? 'p-inputswitch-checked' : ''}`}
            />
          </div>
          <span className="mr-2">
            { rationaleToggle }
          </span>
          <TooltipIcon
            targetClass="custom-response-toggle"
            message="Include a follow-up text box for respondents to provide reasoning for their selection."
          />
        </div>
      </div>
    );
  };

  return (
    <div id={elementId}>
      <Card className="rounded-lg mb-5">
        {warnings.length > 0 && (
          <div className="mx-4 mb-6 px-6 py-4 border-l-[5px] border-[#f3b947] bg-[#fdf1d5] rounded-sm text-black">
            <p className="font-semibold">
              RTR Guidelines:
            </p>
            <ul className="list-disc list-inside text-black">
              {warnings.map((w, index) => (
                <li key={index}>
                  {w}
                </li>
              ))}
            </ul>
            <p className="text-black mt-1 italic">
              Please verify proper RTR display in the Preview Branding window, and make any necessary question or brand adjustments.
            </p>
          </div>
        )}
        <QuestionHeader
          extSurveyId={extSurveyId}
          number={(question?.questionOrder || 0) + 1}
          onDelete={handleDelete}
          onDuplicate={() => {
          }}
        />
        <div className="px-8">
          <div className="mb-6">
            <InputText
              value={questionText}
              placeholder="Enter your question here"
              className={`w-full rounded-lg ${error && "border-red-501"} border border-gray-301 p-2`}
              onChange={handleQuestionTextChange}
              onBlur={handleQuestionTextBlur}
              disabled={data?.surveyStatus === SurveyStatus.Review}
            />
            {error && (
              <p className="mt-1 text-red-501 text-sm">
                {error}
              </p>
            )}
          </div>

          <QuestionImageUpload
            questionId={question?.id || ""}
            extSurveyId={extSurveyId}
            imageSrc={question?.imageUrl || null}
          />
          <RadioButtonGrid
            {...answerTypeProps}
            onClick={handleAnswerTypeChange}
            initialValue={answerType}
            id={id}
          />
          { !isFreeform && (
            <>
              <div className="mt-4 mb-2 flex flex-row items-center justify-between">
                <div className="font-semibold">
                  Settings
                </div>
              </div>
              {renderOptionToggles()}
              <div className="mt-4 mb-2 flex flex-row items-center justify-between">
                <div className="font-semibold">
                  Answers
                </div>
              </div>
              {question?.answers?.map((ans, index) => (
                <AnswerInput
                  key={index}
                  index={index}
                  extSurveyId={extSurveyId}
                  questionId={question.id || ''}
                  answerId={ans.id}
                  isImage={isImageAnswers}
                  className="shadow-sm"
                  isMultiSelect={answerType === SurveyQuestionType.MultiSelect}
                />
              ))}
              {canAddAnswer && (
                <div className="mt-3">
                  <B2BButton
                    icon={plusIcon}
                    label={addAnswerText}
                    theme='light'
                    size='regular-thin'
                    onClick={handleAddOption}
                  />
                </div>
              )
              }
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
